import {
	type ApplicationError,
	ForManagingAccount,
	type UserInformation
} from 'core'

import { HTTPClient, HTTPMethods } from './http_client'
import { UserInformationDTO } from './types'

export class AccountManager implements ForManagingAccount {
	readonly client = new HTTPClient()

	async getUserInfo() {
		const response = await this.client.fetch<UserInformationDTO>(
			`/authv2/home/api/v1/me`
		)

		if (response.error) {
			return { error: response.error }
		}

		const userInfo = this.formatUserInformation(response.data!)

		return {
			data: userInfo
		}
	}

	async signIn(
		authToken: string,
		appCheck: string
	): Promise<{ error?: ApplicationError; status?: number }> {
		const response = await this.client.fetch<
			UserInformationDTO,
			{ id_token: string }
		>(`/authv2/accounts/api/v2/sign_in`, {
			data: {
				id_token: authToken
			},
			method: HTTPMethods.Post,
			headers: {
				'X-Firebase-AppCheck': appCheck
			}
		})

		if (response.error) {
			return { error: response.error, status: response.status }
		}

		return { status: response.status }
	}

	async signUp(payload: {
		first_name?: string
		last_name?: string
		handler: string
		id_token: string
	}) {
		const response = await this.client.fetch<
			UserInformationDTO,
			{
				first_name?: string
				last_name?: string
				user_handler: string
				id_token: string
			}
		>(`/authv2/accounts/api/v1/sign_up`, {
			method: HTTPMethods.Post,
			data: {
				first_name: payload.first_name,
				last_name: payload.last_name,
				user_handler: payload.handler,
				id_token: payload.id_token
			}
		})

		if (response.error) {
			return { error: response.error }
		}

		return {}
	}

	async sendSignInEmail(
		email: string,
		continueUrl: string,
		appCheckToken: string
	): Promise<{ error?: ApplicationError }> {
		return this.client.fetch(`/authv2/accounts/api/v2/sign_in/email`, {
			method: HTTPMethods.Post,
			data: {
				email,
				continue_url: continueUrl
			},
			headers: {
				'X-Firebase-AppCheck': appCheckToken
			}
		})
	}

	async signOut(): Promise<{ error?: ApplicationError }> {
		return this.client.fetch(`/authv2/accounts/api/v2/sign_out`, {
			method: HTTPMethods.Get
		})
	}

	private formatUserInformation(userInfo: UserInformationDTO): UserInformation {
		return {
			isAuthenticated: true,
			firstName: userInfo.first_name,
			lastName: userInfo.last_Name,
			fullName:
				userInfo.first_name && userInfo.last_Name
					? `${userInfo.first_name} ${userInfo.last_Name}`
					: '',
			email: userInfo.email,
			onboarded: userInfo.onboarded,
			wsSubjects: userInfo.ws_subjects,
			userHandler: userInfo.user_handler,
			subscription: {
				id: userInfo.subscription.id,
				active: userInfo.subscription.active,
				currentPlan: {
					id: userInfo.subscription.current_plan.id,
					name: userInfo.subscription.current_plan.name,
					description: userInfo.subscription.current_plan.description
				},
				startDate: userInfo.subscription.start_date,
				endDate: userInfo.subscription.end_date
			}
		}
	}
}

export class AccountManagerMock implements ForManagingAccount {
	getUserInfo() {
		return Promise.resolve({
			data: {
				isAuthenticated: true,
				firstName: 'Ezequiel',
				lastName: 'Martinez',
				fullName: 'Ezequiel Martinez',
				email: 'ezequiel.martinez@codigo.ai',
				onboarded: true,
				wsSubjects: ['broadcast.v1', 'broadcast.v2'],
				userHandler: 'zequee98',
				subscription: {
					id: '018f9d31-3d91-7f25-9e8e-08cda86d75f5',
					active: true,
					currentPlan: {
						id: '2',
						name: 'Life time free plan',
						description: 'Free forever'
					},
					startDate: '2024-05-16 17:26:40.015602 +0000 UTC',
					endDate: '2025-10-10 11:30:30 +0000 UTC'
				}
			}
		})
	}

	async signIn() // _authToken: string
	: Promise<{ data?: UserInformation; error?: ApplicationError }> {
		throw new Error('Method not implemented.')
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	async signUp(_payload: {
		first_name?: string
		last_name?: string
		handler: string
	}): Promise<{ data?: UserInformation; error?: ApplicationError }> {
		throw new Error('Method not implemented.')
	}

	async sendSignInEmail(): Promise<{ error?: ApplicationError }> {
		throw new Error('Method not implemented.')
	}

	async signOut(): Promise<{ error?: ApplicationError }> {
		throw new Error('Method not implemented.')
	}
}
